import {inject, Injectable} from '@angular/core';
import {DBSchema, IDBPDatabase, IDBPTransaction, openDB} from 'idb';
import {Observable, Subject} from 'rxjs';
import {User} from '../../models/user';
import {AppConfigService} from '../../services/config.service';
import {UserService} from '../../services/user.service';
import {TxHistoryFailureReason, TxHistoryMeta, TxHistoryRec, TxHistoryStatus} from './tx-history-rec';

type UserId = User['id'];

const enum IsStoredInApi {
    NO  = '0',
    YES = '1'
}

interface RowInternalFields {
    _userId: UserId; // internal key for isolation
    _encrypted?: boolean;
    _isStoredInApi?: IsStoredInApi; // internal flag for API sync status, index requires to be string
}

interface TxHistoryDB extends DBSchema {
    transactions: {
        key: string; // hash
        value: TxHistoryRec & RowInternalFields;
        indexes: {
            userId: UserId;
            status: [UserId, TxHistoryStatus];
            blockNumber: [UserId, TxHistoryRec['blockNumber']];
            from: [UserId, TxHistoryRec['from']];
            to: [UserId, TxHistoryRec['to']];
            createdAt: [UserId, TxHistoryRec['createdAt']];
            method: [UserId, TxHistoryMeta['method']];
            isStoredInApi: [UserId, IsStoredInApi]
        };
    };
}

const clearTx = (internalRow: TxHistoryRec & RowInternalFields): TxHistoryRec => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const {_userId, _encrypted, _isStoredInApi, ...tx} = internalRow;

    return tx;
};

@Injectable({ providedIn: 'root' })
export class TxUserBrowserStorageService {

    public readonly transactionAdded$: Observable<TxHistoryRec>;
    public readonly transactionUpdated$: Observable<TxHistoryRec>;

    private readonly appConfigService = inject(AppConfigService);
    private readonly userService = inject(UserService);

    private readonly DB_NAME = `${this.appConfigService.appConfig.env}_TxHistory`;
    private readonly STORE_NAME = 'transactions';
    private readonly ENCRYPTION_KEY = 'tx-encryption-key'; // TODO

    private readonly transactionAddedSub$ = new Subject<TxHistoryRec>();
    private readonly transactionUpdatedSubj$ = new Subject<TxHistoryRec>();

    constructor() {
        this.transactionAdded$ = this.transactionAddedSub$.asObservable();
        this.transactionUpdated$ = this.transactionUpdatedSubj$.asObservable();

        // TODO
        (window as any).txUserBrowserStorageService = this; // TODO
        (window as any).txLastToPending = async () => {
            const th = (await this.getAllTransactions())[0];
            if (th) {
                await this.updateTransactionStatus(th.hash, {status: TxHistoryStatus.PENDING});
            }
        };
    }

    public async setTransaction(tx: TxHistoryRec): Promise<void> {
        const db = await this.getDB();

        await db.put(this.STORE_NAME, {
            ...tx,
            _userId: this.getUserId(),
            _isStoredInApi: IsStoredInApi.NO // New transactions are not yet synced to API
            // _encrypted: true // if encryption is enabled
        });

        this.transactionAddedSub$.next(tx);
    }

    public async updateTransactionStatus(
        hash: string,
        updates: {
            status?: TxHistoryStatus,
            failureReason?: TxHistoryFailureReason;
            blockNumber?: bigint | string,
            updatedAt?: number,
            gasUsedNative?: string,
            logs?: TxHistoryRec['logs'],
            logsMeta?: TxHistoryRec['logsMeta'],
            isStoredInApi?: boolean
        }
    ): Promise<TxHistoryRec | null> {
        const db = await this.getDB();
        const existing = await db.get(this.STORE_NAME, hash);
        const userId = this.getUserId();

        if (!existing || existing._userId !== userId) {
            return null; // Not found or not owned by the user
        }

        const updated: TxHistoryRec & RowInternalFields = {
            ...existing,
            status:         updates.status ?? existing.status,
            failureReason:  updates.hasOwnProperty('failureReason') ? updates.failureReason : existing.failureReason,
            blockNumber:    updates.blockNumber ? String(updates.blockNumber) : existing.blockNumber,
            updatedAt:      updates.updatedAt || Date.now(),
            gasUsedNative:  updates.gasUsedNative ?? existing.gasUsedNative,
            logs:           updates.logs ?? existing.logs,
            logsMeta:       updates.logsMeta ?? existing.logsMeta
        };

        if ('isStoredInApi' in updates) {
            updated._isStoredInApi = updates.isStoredInApi ? IsStoredInApi.YES : IsStoredInApi.NO;
        }

        await db.put(this.STORE_NAME, {
            ...updated,
            _userId: userId
        });

        this.transactionUpdatedSubj$.next(clearTx(updated));

        return clearTx(updated);
    }

    public async markTransactionAsStoredInApi(hash: string): Promise<TxHistoryRec | null> {
        const search = await this.getInternalTransactionByHash(hash);

        if (!search) {
            return null;
        }

        const {tx, db} = search;

        const updated: TxHistoryRec & RowInternalFields = {
            ...tx,
            _isStoredInApi: IsStoredInApi.YES
        };

        await db.put(this.STORE_NAME, updated);

        return clearTx(updated);
    }

    public async getTransactionByHash(hash: string): Promise<TxHistoryRec | null> {
        const tx = (await this.getInternalTransactionByHash(hash))?.tx;

        return tx && clearTx(tx);
    }

    public async deleteTransaction(hash: string): Promise<boolean> {
        const db = await this.getDB();
        const existing = await db.get(this.STORE_NAME, hash);

        if (!existing || existing._userId !== this.getUserId()) {
            return false;
        }

        await db.delete(this.STORE_NAME, hash);

        return true;
    }

    public async getAllTransactions(): Promise<TxHistoryRec[]> {
        const db = await this.getDB();
        const index = db.transaction(this.STORE_NAME, 'readonly').store.index('userId');
        const all = await index.getAll(this.getUserId());

        return all
            .sort((a, b) => b.createdAt - a.createdAt)
            .map(clearTx);
    }

    public async getPendingTransactions(): Promise<TxHistoryRec[]> {
        const db = await this.getDB();
        const index = db.transaction(this.STORE_NAME, 'readonly').store.index('status');
        const pending = await index.getAll([this.getUserId(), TxHistoryStatus.PENDING]);

        return pending
            .sort((a, b) => b.createdAt - a.createdAt)
            .map(clearTx);
    }

    public async getConfirmedTransactions(): Promise<TxHistoryRec[]> {
        const db = await this.getDB();
        const index = db.transaction(this.STORE_NAME).store.index('status');
        const pending = await index.getAll([this.getUserId(), TxHistoryStatus.CONFIRMED]);

        return pending
            .sort((a, b) => b.createdAt - a.createdAt)
            .map(clearTx);
    }

    public async getFailedTransactions(filterFn?: (th: TxHistoryRec) => boolean): Promise<TxHistoryRec[]> {
        const db = await this.getDB();
        const index = db.transaction(this.STORE_NAME).store.index('status');
        let pending = await index.getAll([this.getUserId(), TxHistoryStatus.FAILED]);

        if (filterFn) {
            pending = pending.filter(filterFn);
        }

        return pending
            .sort((a, b) => b.createdAt - a.createdAt)
            .map(clearTx);
    }

    public async getTransactionsToMonitor(): Promise<TxHistoryRec[]> {
        const db = await this.getDB();
        const index = db.transaction(this.STORE_NAME, 'readonly').store.index('status');
        const userId = this.getUserId();

        const txs = (await Promise.all([
            index.getAll([userId, TxHistoryStatus.PENDING]),
            index.getAll([userId, TxHistoryStatus.CONFIRMED])
        ])).flat();

        return txs
            .sort((a, b) => b.createdAt - a.createdAt)
            .map(clearTx);
    }

    public async hasTransactionsToMonitor(failureToIncludeFilterFn?: (th: TxHistoryRec) => boolean): Promise<boolean> {
        const db = await this.getDB();
        const index = db.transaction(this.STORE_NAME, 'readonly').store.index('status');
        const userId = this.getUserId();

        let thKey = (await index.getKey([userId, TxHistoryStatus.PENDING]));
        thKey = thKey || (await index.getKey([userId, TxHistoryStatus.CONFIRMED]));

        if (!thKey && failureToIncludeFilterFn) {
            for await (const cursor of index.iterate([this.getUserId(), TxHistoryStatus.FAILED])) {
                const th = cursor.value;

                if (failureToIncludeFilterFn(th)) {
                    thKey = cursor.primaryKey;
                    break;
                }
            }
        }

        return !!thKey;
    }

    public async getTransactionsByAddress(
        address: string,
        limit = 100
    ): Promise<TxHistoryRec[]> {
        const db = await this.getDB();
        const fromIndex = db.transaction(this.STORE_NAME, 'readonly').store.index('from');
        const toIndex = db.transaction(this.STORE_NAME, 'readonly').store.index('to');
        const userId = this.getUserId();

        const [sent, received] = await Promise.all([
            fromIndex.getAll([userId, address]),
            toIndex.getAll([userId, address])
        ]);

        return [...sent, ...received]
            .sort((a, b) => b.createdAt - a.createdAt)
            .slice(0, limit)
            .map(clearTx);
    }

    public async getTransactionsByBlockRange(
        fromBlock: number,
        toBlock: number
    ): Promise<TxHistoryRec[]> {
        const db = await this.getDB();
        const index = db.transaction(this.STORE_NAME, 'readonly').store.index('blockNumber');
        const userId = this.getUserId();

        const txs = await index.getAll(
            IDBKeyRange.bound(
                [userId, fromBlock],
                [userId, toBlock]
            )
        );

        return txs
            .sort((a, b) => b.createdAt - a.createdAt)
            .map(clearTx);
    }

    public async getTransactionsByMethod(method: TxHistoryMeta['method']): Promise<TxHistoryRec[]> {
        const db = await this.getDB();
        const index = db.transaction(this.STORE_NAME, 'readonly').store.index('method');
        const pending = await index.getAll([this.getUserId(), method]);

        return pending
            .sort((a, b) => b.createdAt - a.createdAt)
            .map(clearTx);
    }

    /**
     * Get transactions that need to be synced to API (_isStoredInApi = false)
     */
    public async getTransactionsNotSyncedToApi(): Promise<TxHistoryRec[]> {
        const db = await this.getDB();
        const index = db.transaction(this.STORE_NAME, 'readonly').store.index('isStoredInApi');
        const notSynced = await index.getAll([this.getUserId(), IsStoredInApi.NO]);

        return notSynced
            .sort((a, b) => b.createdAt - a.createdAt)
            .map(clearTx);
    }

    /**
     * Check if there are transactions that need to be synced to API
     */
    public async hasTransactionsNotSyncedToApi(): Promise<boolean> {
        const db = await this.getDB();
        const index = db.transaction(this.STORE_NAME, 'readonly').store.index('isStoredInApi');
        const hasNotSynced = await index.getKey([this.getUserId(), IsStoredInApi.NO]);

        return !!hasNotSynced;
    }

    public async setTransactions(txs: TxHistoryRec[]): Promise<void> {
        const db = await this.getDB();
        const userId = this.getUserId();

        const tx = db.transaction(this.STORE_NAME, 'readwrite');
        await Promise.all(
            txs.map(t => {
                return tx.store.put({
                    ...t,
                    _userId: userId,
                    _isStoredInApi: IsStoredInApi.NO // Default to false for batch operations
                });
            })
        );
        await tx.done;
    }

    public async clearUserTransactions(): Promise<void> {
        const db = await this.getDB();
        const index = db.transaction(this.STORE_NAME, 'readwrite').store.index('userId');
        const keys = await index.getAllKeys(this.getUserId());

        const tx = db.transaction(this.STORE_NAME, 'readwrite');
        await Promise.all(keys.map(k => tx.store.delete(k)));
        await tx.done;
    }

    public async getInternalTransactionByHash(hash: string): Promise<{
        tx: TxHistoryRec & RowInternalFields;
        db: IDBPDatabase<TxHistoryDB>;
    } | null> {
        const db = await this.getDB();
        const tx = await db.get(this.STORE_NAME, hash);

        if (!tx || tx._userId !== this.getUserId()) {
            return null;
        }

        return {tx, db};
    }

    private async encrypt(data: Partial<TxHistoryRec>): Promise<string> {
        const key = await this.getEncryptionKey();
        const encoder = new TextEncoder();
        const ciphertext = await crypto.subtle.encrypt(
            { name: 'AES-GCM', iv: new Uint8Array(12) },
            key,
            encoder.encode(JSON.stringify(data))
        );
        return btoa(String.fromCharCode(...new Uint8Array(ciphertext)));
    }

    private async decrypt<T>(encrypted: string): Promise<T> {
        const key = await this.getEncryptionKey();
        const decrypted = await crypto.subtle.decrypt(
            { name: 'AES-GCM', iv: new Uint8Array(12) },
            key,
            new Uint8Array(atob(encrypted).split('').map(c => c.charCodeAt(0)))
        );
        return JSON.parse(new TextDecoder().decode(decrypted));
    }

    private async getEncryptionKey(): Promise<CryptoKey> {
        throw new Error('Implement encryption key derivation');
    }

    private async getDB(): Promise<IDBPDatabase<TxHistoryDB>> {
        return openDB<TxHistoryDB>(this.DB_NAME, 3, { // version 3 - added isStoredInApi support
            upgrade: async (db, oldVersion, _newVersion, tx) => {
                if (oldVersion < 1) {
                    const store = db.createObjectStore(this.STORE_NAME, {keyPath: 'hash'});

                    // Index for fast clearing user data
                    store.createIndex('userId', '_userId');

                    // Filters indexes
                    store.createIndex('status', ['_userId', 'status']);
                    store.createIndex('blockNumber', ['_userId', 'blockNumber']);
                    store.createIndex('from', ['_userId', 'from']);
                    store.createIndex('to', ['_userId', 'to']);
                    store.createIndex('createdAt', ['_userId', 'createdAt']);
                    store.createIndex('method', ['_userId', 'meta.method']);
                    store.createIndex('isStoredInApi', ['_userId', '_isStoredInApi']);
                }

                if (oldVersion < 3) {
                    if (oldVersion >= 1) {
                        const store = tx.objectStore(this.STORE_NAME);
                        store.createIndex('isStoredInApi', ['_userId', '_isStoredInApi']);
                    }

                    await this.migrateToVersion3(tx);
                }
            }
        });
    }

    /**
     * Migration from version 2 to 3: Add _isStoredInApi field to existing records
     */
    private async migrateToVersion3(
        tx: IDBPTransaction<TxHistoryDB, ['transactions'], "versionchange">
    ): Promise<void> {
        const store = tx.store;
        let cursor = await store.openCursor();

        while (cursor) {
            const txRecord = cursor.value;

            // Add _isStoredInApi field if it doesn't exist
            if (txRecord._isStoredInApi === undefined) {
                const updatedRecord = {
                    ...txRecord,
                    _isStoredInApi: IsStoredInApi.NO // Default to false for existing records
                };
                await cursor.update(updatedRecord);
            }

            cursor = await cursor.continue();
        }
    }

    private getUserId(): UserId {
        const userId = this.userService.user?.id;

        if (!userId) throw new Error('User not authenticated');

        return userId;
    }

}
